import { useEffect } from "react";

const canonicalUrl = "https://sailplot.app/";
const description =
  "SailPlot is the sailing tactics board for sailors, coaches, teams, and clubs. Plot. Discuss. Learn. Powered by Heisel Analytics.";

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://sailplot.app/#website",
      url: canonicalUrl,
      name: "SailPlot",
      alternateName: "SailPlot.app",
      description,
      inLanguage: ["en", "de"],
    },
    {
      "@type": "WebApplication",
      "@id": "https://sailplot.app/#application",
      name: "SailPlot",
      url: canonicalUrl,
      description,
      applicationCategory: "SportsApplication",
      operatingSystem: "Any",
      browserRequirements:
        "Requires a modern web browser with JavaScript enabled.",
      image: "https://sailplot.app/icons/sailplot-icon.svg",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
      },
      creator: {
        "@type": "Organization",
        name: "Heisel Analytics",
        url: "https://heiselanalytics.one/",
      },
    },
  ],
};

const setMeta = (selector: string, attributes: Record<string, string>) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    document.head.append(element);
  }
  for (const [name, value] of Object.entries(attributes)) {
    element.setAttribute(name, value);
  }
};

const setCanonical = () => {
  let element = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.append(element);
  }
  element.href = canonicalUrl;
};

const setStructuredData = () => {
  let element = document.head.querySelector<HTMLScriptElement>(
    "#sailplot-structured-data",
  );
  if (!element) {
    element = document.createElement("script");
    element.type = "application/ld+json";
    element.id = "sailplot-structured-data";
    document.head.append(element);
  }
  element.textContent = JSON.stringify(structuredData);
};

export function SeoMetadata({
  tenantId,
  currentPath,
  configuratorPreview,
}: {
  tenantId: string;
  currentPath: string;
  configuratorPreview: boolean;
}) {
  useEffect(() => {
    const canonicalHost =
      window.location.hostname === "sailplot.app" ||
      window.location.hostname === "www.sailplot.app";
    const indexable =
      canonicalHost &&
      tenantId === "sailplot" &&
      currentPath === "/" &&
      !configuratorPreview;

    if (!indexable) {
      const robots = configuratorPreview
        ? "noindex, nofollow, noarchive"
        : "noindex, follow";
      setMeta('meta[name="robots"]', { name: "robots", content: robots });
      setMeta('meta[name="googlebot"]', {
        name: "googlebot",
        content: robots,
      });
      document.head.querySelector('link[rel="canonical"]')?.remove();
      document.head.querySelector("#sailplot-structured-data")?.remove();
      return;
    }

    setMeta('meta[name="robots"]', {
      name: "robots",
      content:
        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    setMeta('meta[name="googlebot"]', {
      name: "googlebot",
      content:
        "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    });
    setMeta('meta[name="description"]', {
      name: "description",
      content: description,
    });
    setCanonical();
    setStructuredData();
  }, [configuratorPreview, currentPath, tenantId]);

  return null;
}
